export interface CountryData {
  id: string;
  code: string;
  name: string;
  flag: string;
  coordinates: [number, number];
  avgSalary: number;
  avgSalaryRange: string;
  costOfLivingIndex: number;
  taxRates: {
    income: number[];
    social: number;
    vat: number;
  };
  topEmployers: {
    name: string;
    avgSalary: number;
    jobCount: number;
  }[];
  topPayingJobs: {
    title: string;
    avgSalary: number;
    jobCount: number;
    growthRate: number;
  }[];
  livingCosts: {
    rent: number;
    food: number;
    transport: number;
    utilities: number;
    entertainment: number;
    healthcare: number;
  };
  marketStats: {
    totalJobs: number;
    remoteJobs: number;
    averageWorkWeek: number;
    vacationDays: number;
    healthcare: 'public' | 'private' | 'mixed';
    jobSecurity: number; // 1-10 scale
    workLifeBalance: number; // 1-10 scale
    techJobGrowth: number; // percentage
  };
  economicIndicators: {
    gdpPerCapita: number;
    unemploymentRate: number;
    inflationRate: number;
    currency: string;
    currencySymbol: string;
  };
  qualityOfLife: {
    safetyIndex: number;
    healthcareIndex: number;
    educationIndex: number;
    environmentIndex: number;
  };
}

export const countryData: CountryData[] = [
  {
    id: 'usa',
    code: 'US',
    name: 'United States',
    flag: '🇺🇸',
    coordinates: [-95.7129, 37.0902],
    avgSalary: 75000,
    avgSalaryRange: '$60k-$120k',
    costOfLivingIndex: 100,
    taxRates: {
      income: [12, 22, 24, 32, 35, 37],
      social: 7.65,
      vat: 0
    },
    topEmployers: [
      { name: 'Google', avgSalary: 150000, jobCount: 15000 },
      { name: 'Microsoft', avgSalary: 140000, jobCount: 12000 },
      { name: 'Apple', avgSalary: 145000, jobCount: 10000 },
      { name: 'Amazon', avgSalary: 130000, jobCount: 20000 },
      { name: 'Meta', avgSalary: 155000, jobCount: 8000 }
    ],
    topPayingJobs: [
      { title: 'Software Engineer', avgSalary: 120000, jobCount: 45000, growthRate: 15.2 },
      { title: 'Data Scientist', avgSalary: 115000, jobCount: 25000, growthRate: 22.1 },
      { title: 'Product Manager', avgSalary: 130000, jobCount: 18000, growthRate: 12.5 },
      { title: 'Investment Banker', avgSalary: 140000, jobCount: 8000, growthRate: 5.2 },
      { title: 'Surgeon', avgSalary: 300000, jobCount: 12000, growthRate: 8.1 }
    ],
    livingCosts: {
      rent: 1500,
      food: 400,
      transport: 200,
      utilities: 150,
      entertainment: 200,
      healthcare: 300
    },
    marketStats: {
      totalJobs: 180000,
      remoteJobs: 65000,
      averageWorkWeek: 40,
      vacationDays: 15,
      healthcare: 'private',
      jobSecurity: 6.5,
      workLifeBalance: 6.8,
      techJobGrowth: 18.5
    },
    economicIndicators: {
      gdpPerCapita: 65280,
      unemploymentRate: 3.7,
      inflationRate: 3.2,
      currency: 'USD',
      currencySymbol: '$'
    },
    qualityOfLife: {
      safetyIndex: 7.2,
      healthcareIndex: 8.1,
      educationIndex: 8.9,
      environmentIndex: 6.8
    }
  },
  {
    id: 'uk',
    code: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧',
    coordinates: [-3.436, 55.3781],
    avgSalary: 55000,
    avgSalaryRange: '£40k-£80k',
    costOfLivingIndex: 110,
    taxRates: {
      income: [20, 40, 45],
      social: 12,
      vat: 20
    },
    topEmployers: [
      { name: 'Barclays', avgSalary: 85000, jobCount: 8500 },
      { name: 'HSBC', avgSalary: 90000, jobCount: 7200 },
      { name: 'BP', avgSalary: 80000, jobCount: 6800 },
      { name: 'GSK', avgSalary: 75000, jobCount: 5400 },
      { name: 'Rolls-Royce', avgSalary: 70000, jobCount: 4200 }
    ],
    topPayingJobs: [
      { title: 'Investment Banker', avgSalary: 120000, jobCount: 12000, growthRate: 8.5 },
      { title: 'Software Architect', avgSalary: 90000, jobCount: 18000, growthRate: 14.2 },
      { title: 'Medical Consultant', avgSalary: 100000, jobCount: 8500, growthRate: 6.1 },
      { title: 'Corporate Lawyer', avgSalary: 95000, jobCount: 6200, growthRate: 4.8 },
      { title: 'Data Engineer', avgSalary: 80000, jobCount: 15000, growthRate: 19.3 }
    ],
    livingCosts: {
      rent: 1200,
      food: 350,
      transport: 150,
      utilities: 120,
      entertainment: 180,
      healthcare: 0
    },
    marketStats: {
      totalJobs: 95000,
      remoteJobs: 38000,
      averageWorkWeek: 37.5,
      vacationDays: 28,
      healthcare: 'public',
      jobSecurity: 7.8,
      workLifeBalance: 8.2,
      techJobGrowth: 16.8
    },
    economicIndicators: {
      gdpPerCapita: 42330,
      unemploymentRate: 4.2,
      inflationRate: 4.1,
      currency: 'GBP',
      currencySymbol: '£'
    },
    qualityOfLife: {
      safetyIndex: 8.1,
      healthcareIndex: 9.2,
      educationIndex: 8.8,
      environmentIndex: 7.4
    }
  },
  {
    id: 'germany',
    code: 'DE',
    name: 'Germany',
    flag: '🇩🇪',
    coordinates: [10.4515, 51.1657],
    avgSalary: 60000,
    avgSalaryRange: '€45k-€90k',
    costOfLivingIndex: 85,
    taxRates: {
      income: [14, 42, 45],
      social: 20,
      vat: 19
    },
    topEmployers: [
      { name: 'SAP', avgSalary: 85000, jobCount: 12500 },
      { name: 'BMW', avgSalary: 80000, jobCount: 9800 },
      { name: 'Siemens', avgSalary: 75000, jobCount: 11200 },
      { name: 'Mercedes-Benz', avgSalary: 78000, jobCount: 8600 },
      { name: 'Volkswagen', avgSalary: 72000, jobCount: 7400 }
    ],
    topPayingJobs: [
      { title: 'Engineering Manager', avgSalary: 95000, jobCount: 15000, growthRate: 11.2 },
      { title: 'IT Architect', avgSalary: 85000, jobCount: 12500, growthRate: 16.8 },
      { title: 'Medical Specialist', avgSalary: 110000, jobCount: 8200, growthRate: 7.3 },
      { title: 'Finance Director', avgSalary: 100000, jobCount: 5800, growthRate: 5.9 },
      { title: 'Sales Director', avgSalary: 90000, jobCount: 9200, growthRate: 8.1 }
    ],
    livingCosts: {
      rent: 900,
      food: 300,
      transport: 100,
      utilities: 200,
      entertainment: 150,
      healthcare: 100
    },
    marketStats: {
      totalJobs: 125000,
      remoteJobs: 32000,
      averageWorkWeek: 35,
      vacationDays: 30,
      healthcare: 'mixed',
      jobSecurity: 8.5,
      workLifeBalance: 9.1,
      techJobGrowth: 14.2
    },
    economicIndicators: {
      gdpPerCapita: 46230,
      unemploymentRate: 3.1,
      inflationRate: 2.8,
      currency: 'EUR',
      currencySymbol: '€'
    },
    qualityOfLife: {
      safetyIndex: 8.8,
      healthcareIndex: 8.9,
      educationIndex: 8.6,
      environmentIndex: 8.2
    }
  },
  {
    id: 'canada',
    code: 'CA',
    name: 'Canada',
    flag: '🇨🇦',
    coordinates: [-106.3468, 56.1304],
    avgSalary: 65000,
    avgSalaryRange: 'C$50k-C$100k',
    costOfLivingIndex: 95,
    taxRates: {
      income: [15, 20.5, 26, 29, 33],
      social: 5.7,
      vat: 5
    },
    topEmployers: [
      { name: 'Shopify', avgSalary: 110000, jobCount: 8500 },
      { name: 'RBC', avgSalary: 90000, jobCount: 12000 },
      { name: 'TD Bank', avgSalary: 85000, jobCount: 10500 },
      { name: 'Bell', avgSalary: 75000, jobCount: 9200 },
      { name: 'Bombardier', avgSalary: 80000, jobCount: 6800 }
    ],
    topPayingJobs: [
      { title: 'Software Developer', avgSalary: 90000, jobCount: 28000, growthRate: 17.5 },
      { title: 'Petroleum Engineer', avgSalary: 110000, jobCount: 4200, growthRate: 3.2 },
      { title: 'Pharmacist', avgSalary: 95000, jobCount: 8500, growthRate: 6.8 },
      { title: 'Financial Manager', avgSalary: 85000, jobCount: 12000, growthRate: 9.1 },
      { title: 'Dentist', avgSalary: 120000, jobCount: 6200, growthRate: 5.4 }
    ],
    livingCosts: {
      rent: 1300,
      food: 350,
      transport: 120,
      utilities: 130,
      entertainment: 170,
      healthcare: 0
    },
    marketStats: {
      totalJobs: 85000,
      remoteJobs: 34000,
      averageWorkWeek: 37.5,
      vacationDays: 20,
      healthcare: 'public',
      jobSecurity: 8.2,
      workLifeBalance: 8.7,
      techJobGrowth: 19.2
    },
    economicIndicators: {
      gdpPerCapita: 43240,
      unemploymentRate: 5.8,
      inflationRate: 3.8,
      currency: 'CAD',
      currencySymbol: 'C$'
    },
    qualityOfLife: {
      safetyIndex: 8.9,
      healthcareIndex: 8.8,
      educationIndex: 8.7,
      environmentIndex: 8.5
    }
  },
  {
    id: 'netherlands',
    code: 'NL',
    name: 'Netherlands',
    flag: '🇳🇱',
    coordinates: [5.2913, 52.1326],
    avgSalary: 55000,
    avgSalaryRange: '€40k-€80k',
    costOfLivingIndex: 88,
    taxRates: {
      income: [37.07, 49.5],
      social: 27.65,
      vat: 21
    },
    topEmployers: [
      { name: 'ASML', avgSalary: 90000, jobCount: 6500 },
      { name: 'Philips', avgSalary: 75000, jobCount: 8200 },
      { name: 'ING', avgSalary: 80000, jobCount: 9800 },
      { name: 'Shell', avgSalary: 85000, jobCount: 7400 },
      { name: 'Booking.com', avgSalary: 95000, jobCount: 5200 }
    ],
    topPayingJobs: [
      { title: 'Software Engineer', avgSalary: 75000, jobCount: 18000, growthRate: 16.8 },
      { title: 'Data Scientist', avgSalary: 80000, jobCount: 8500, growthRate: 21.3 },
      { title: 'Medical Specialist', avgSalary: 100000, jobCount: 6200, growthRate: 8.1 },
      { title: 'Legal Advisor', avgSalary: 85000, jobCount: 4800, growthRate: 6.2 },
      { title: 'Project Manager', avgSalary: 70000, jobCount: 12000, growthRate: 11.5 }
    ],
    livingCosts: {
      rent: 1100,
      food: 300,
      transport: 100,
      utilities: 150,
      entertainment: 180,
      healthcare: 110
    },
    marketStats: {
      totalJobs: 65000,
      remoteJobs: 28000,
      averageWorkWeek: 36,
      vacationDays: 25,
      healthcare: 'mixed',
      jobSecurity: 8.1,
      workLifeBalance: 9.2,
      techJobGrowth: 18.5
    },
    economicIndicators: {
      gdpPerCapita: 52330,
      unemploymentRate: 3.8,
      inflationRate: 3.1,
      currency: 'EUR',
      currencySymbol: '€'
    },
    qualityOfLife: {
      safetyIndex: 8.7,
      healthcareIndex: 8.6,
      educationIndex: 8.8,
      environmentIndex: 8.4
    }
  },
  {
    id: 'poland',
    code: 'PL',
    name: 'Poland',
    flag: '🇵🇱',
    coordinates: [19.1343, 51.9194],
    avgSalary: 28000,
    avgSalaryRange: '€20k-€45k',
    costOfLivingIndex: 55,
    taxRates: {
      income: [17, 32],
      social: 13.7,
      vat: 23
    },
    topEmployers: [
      { name: 'CD Projekt', avgSalary: 45000, jobCount: 1200 },
      { name: 'Allegro', avgSalary: 42000, jobCount: 2800 },
      { name: 'PKO Bank Polski', avgSalary: 35000, jobCount: 18000 },
      { name: 'PZU', avgSalary: 38000, jobCount: 12000 },
      { name: 'Orange Polska', avgSalary: 40000, jobCount: 8500 }
    ],
    topPayingJobs: [
      { title: 'Senior Software Engineer', avgSalary: 55000, jobCount: 8500, growthRate: 18.2 },
      { title: 'Investment Banking Analyst', avgSalary: 48000, jobCount: 1200, growthRate: 12.1 },
      { title: 'Medical Specialist', avgSalary: 42000, jobCount: 5500, growthRate: 8.5 },
      { title: 'Financial Controller', avgSalary: 45000, jobCount: 3200, growthRate: 10.2 },
      { title: 'Business Consultant', avgSalary: 40000, jobCount: 4800, growthRate: 14.8 }
    ],
    livingCosts: {
      rent: 600,
      food: 250,
      transport: 80,
      utilities: 120,
      entertainment: 100,
      healthcare: 50
    },
    marketStats: {
      totalJobs: 42000,
      remoteJobs: 18500,
      averageWorkWeek: 40,
      vacationDays: 26,
      healthcare: 'public',
      jobSecurity: 7.2,
      workLifeBalance: 7.8,
      techJobGrowth: 16.5
    },
    economicIndicators: {
      gdpPerCapita: 15420,
      unemploymentRate: 2.9,
      inflationRate: 5.1,
      currency: 'PLN',
      currencySymbol: 'zł'
    },
    qualityOfLife: {
      safetyIndex: 7.8,
      healthcareIndex: 6.8,
      educationIndex: 7.4,
      environmentIndex: 6.9
    }
  },
  {
    id: 'czech-republic',
    code: 'CZ',
    name: 'Czech Republic',
    flag: '🇨🇿',
    coordinates: [15.4729, 49.8175],
    avgSalary: 32000,
    avgSalaryRange: '€22k-€50k',
    costOfLivingIndex: 58,
    taxRates: {
      income: [15, 23],
      social: 11,
      vat: 21
    },
    topEmployers: [
      { name: 'Škoda Auto', avgSalary: 48000, jobCount: 8500 },
      { name: 'ČEZ', avgSalary: 45000, jobCount: 6200 },
      { name: 'Avast', avgSalary: 55000, jobCount: 1800 },
      { name: 'JetBrains', avgSalary: 60000, jobCount: 900 },
      { name: 'Societe Generale', avgSalary: 42000, jobCount: 3200 }
    ],
    topPayingJobs: [
      { title: 'Software Architect', avgSalary: 65000, jobCount: 2800, growthRate: 19.5 },
      { title: 'Investment Analyst', avgSalary: 50000, jobCount: 1500, growthRate: 14.2 },
      { title: 'Medical Doctor', avgSalary: 45000, jobCount: 4200, growthRate: 6.8 },
      { title: 'Engineering Manager', avgSalary: 58000, jobCount: 2200, growthRate: 16.1 },
      { title: 'Management Consultant', avgSalary: 48000, jobCount: 1800, growthRate: 12.5 }
    ],
    livingCosts: {
      rent: 650,
      food: 280,
      transport: 90,
      utilities: 140,
      entertainment: 120,
      healthcare: 80
    },
    marketStats: {
      totalJobs: 28000,
      remoteJobs: 12500,
      averageWorkWeek: 40,
      vacationDays: 25,
      healthcare: 'public',
      jobSecurity: 7.8,
      workLifeBalance: 8.1,
      techJobGrowth: 18.2
    },
    economicIndicators: {
      gdpPerCapita: 23350,
      unemploymentRate: 2.4,
      inflationRate: 3.2,
      currency: 'CZK',
      currencySymbol: 'Kč'
    },
    qualityOfLife: {
      safetyIndex: 8.2,
      healthcareIndex: 7.8,
      educationIndex: 7.9,
      environmentIndex: 7.1
    }
  },
  {
    id: 'hungary',
    code: 'HU',
    name: 'Hungary',
    flag: '🇭🇺',
    coordinates: [19.5033, 47.1625],
    avgSalary: 26000,
    avgSalaryRange: '€18k-€42k',
    costOfLivingIndex: 52,
    taxRates: {
      income: [15, 15],
      social: 18.5,
      vat: 27
    },
    topEmployers: [
      { name: 'MOL Group', avgSalary: 42000, jobCount: 5500 },
      { name: 'Vodafone Hungary', avgSalary: 38000, jobCount: 2800 },
      { name: 'EPAM', avgSalary: 48000, jobCount: 3200 },
      { name: 'NNG', avgSalary: 45000, jobCount: 800 },
      { name: 'OTP Bank', avgSalary: 35000, jobCount: 8500 }
    ],
    topPayingJobs: [
      { title: 'Senior Developer', avgSalary: 52000, jobCount: 4200, growthRate: 20.1 },
      { title: 'Financial Analyst', avgSalary: 38000, jobCount: 2500, growthRate: 11.8 },
      { title: 'Medical Specialist', avgSalary: 35000, jobCount: 3800, growthRate: 7.2 },
      { title: 'Project Manager', avgSalary: 42000, jobCount: 3200, growthRate: 13.5 },
      { title: 'Business Analyst', avgSalary: 35000, jobCount: 2800, growthRate: 15.2 }
    ],
    livingCosts: {
      rent: 500,
      food: 220,
      transport: 70,
      utilities: 110,
      entertainment: 90,
      healthcare: 60
    },
    marketStats: {
      totalJobs: 22000,
      remoteJobs: 9500,
      averageWorkWeek: 40,
      vacationDays: 25,
      healthcare: 'public',
      jobSecurity: 7.1,
      workLifeBalance: 7.6,
      techJobGrowth: 17.8
    },
    economicIndicators: {
      gdpPerCapita: 16731,
      unemploymentRate: 3.7,
      inflationRate: 5.1,
      currency: 'HUF',
      currencySymbol: 'Ft'
    },
    qualityOfLife: {
      safetyIndex: 7.5,
      healthcareIndex: 6.9,
      educationIndex: 7.2,
      environmentIndex: 6.8
    }
  },
  {
    id: 'romania',
    code: 'RO',
    name: 'Romania',
    flag: '🇷🇴',
    coordinates: [24.9668, 45.9432],
    avgSalary: 24000,
    avgSalaryRange: '€16k-€38k',
    costOfLivingIndex: 48,
    taxRates: {
      income: [10, 10],
      social: 25,
      vat: 19
    },
    topEmployers: [
      { name: 'eMAG', avgSalary: 35000, jobCount: 2800 },
      { name: 'UiPath', avgSalary: 65000, jobCount: 3200 },
      { name: 'Zitec', avgSalary: 42000, jobCount: 1500 },
      { name: 'Banca Transilvania', avgSalary: 28000, jobCount: 8500 },
      { name: 'Orange Romania', avgSalary: 32000, jobCount: 4200 }
    ],
    topPayingJobs: [
      { title: 'AI Engineer', avgSalary: 58000, jobCount: 1800, growthRate: 25.2 },
      { title: 'Senior Software Engineer', avgSalary: 48000, jobCount: 5500, growthRate: 19.8 },
      { title: 'Investment Banker', avgSalary: 42000, jobCount: 800, growthRate: 15.1 },
      { title: 'Medical Doctor', avgSalary: 32000, jobCount: 4200, growthRate: 8.5 },
      { title: 'Business Consultant', avgSalary: 38000, jobCount: 2200, growthRate: 16.8 }
    ],
    livingCosts: {
      rent: 450,
      food: 200,
      transport: 60,
      utilities: 100,
      entertainment: 80,
      healthcare: 40
    },
    marketStats: {
      totalJobs: 28000,
      remoteJobs: 14500,
      averageWorkWeek: 40,
      vacationDays: 23,
      healthcare: 'public',
      jobSecurity: 6.8,
      workLifeBalance: 7.2,
      techJobGrowth: 21.5
    },
    economicIndicators: {
      gdpPerCapita: 12919,
      unemploymentRate: 5.1,
      inflationRate: 5.8,
      currency: 'RON',
      currencySymbol: 'lei'
    },
    qualityOfLife: {
      safetyIndex: 7.1,
      healthcareIndex: 5.8,
      educationIndex: 6.9,
      environmentIndex: 6.2
    }
  },
  {
    id: 'slovakia',
    code: 'SK',
    name: 'Slovakia',
    flag: '🇸🇰',
    coordinates: [19.6990, 48.6690],
    avgSalary: 30000,
    avgSalaryRange: '€20k-€48k',
    costOfLivingIndex: 56,
    taxRates: {
      income: [19, 25],
      social: 13.4,
      vat: 20
    },
    topEmployers: [
      { name: 'Volkswagen Slovakia', avgSalary: 42000, jobCount: 12500 },
      { name: 'Slovenská sporiteľňa', avgSalary: 38000, jobCount: 4200 },
      { name: 'ESET', avgSalary: 52000, jobCount: 1800 },
      { name: 'Jaguar Land Rover', avgSalary: 45000, jobCount: 3500 },
      { name: 'IBM Slovakia', avgSalary: 48000, jobCount: 2200 }
    ],
    topPayingJobs: [
      { title: 'Software Engineer', avgSalary: 50000, jobCount: 3800, growthRate: 18.5 },
      { title: 'Automotive Engineer', avgSalary: 45000, jobCount: 5200, growthRate: 12.2 },
      { title: 'Financial Analyst', avgSalary: 38000, jobCount: 2800, growthRate: 14.1 },
      { title: 'Medical Specialist', avgSalary: 35000, jobCount: 2200, growthRate: 7.8 },
      { title: 'Quality Manager', avgSalary: 42000, jobCount: 1800, growthRate: 11.5 }
    ],
    livingCosts: {
      rent: 550,
      food: 250,
      transport: 75,
      utilities: 120,
      entertainment: 100,
      healthcare: 70
    },
    marketStats: {
      totalJobs: 18000,
      remoteJobs: 7500,
      averageWorkWeek: 40,
      vacationDays: 25,
      healthcare: 'public',
      jobSecurity: 7.5,
      workLifeBalance: 7.9,
      techJobGrowth: 16.2
    },
    economicIndicators: {
      gdpPerCapita: 19583,
      unemploymentRate: 6.1,
      inflationRate: 3.1,
      currency: 'EUR',
      currencySymbol: '€'
    },
    qualityOfLife: {
      safetyIndex: 7.8,
      healthcareIndex: 7.1,
      educationIndex: 7.6,
      environmentIndex: 7.2
    }
  },
  {
    id: 'slovenia',
    code: 'SI',
    name: 'Slovenia',
    flag: '🇸🇮',
    coordinates: [14.9955, 46.1512],
    avgSalary: 35000,
    avgSalaryRange: '€25k-€55k',
    costOfLivingIndex: 65,
    taxRates: {
      income: [16, 27, 34, 39, 50],
      social: 22.1,
      vat: 22
    },
    topEmployers: [
      { name: 'Krka', avgSalary: 42000, jobCount: 5500 },
      { name: 'Petrol', avgSalary: 45000, jobCount: 3200 },
      { name: 'Outfit7', avgSalary: 55000, jobCount: 800 },
      { name: 'Lek', avgSalary: 48000, jobCount: 2200 },
      { name: 'Nova Ljubljanska banka', avgSalary: 40000, jobCount: 2800 }
    ],
    topPayingJobs: [
      { title: 'Software Engineer', avgSalary: 52000, jobCount: 2800, growthRate: 17.8 },
      { title: 'Pharmaceutical Researcher', avgSalary: 48000, jobCount: 1500, growthRate: 12.5 },
      { title: 'Financial Manager', avgSalary: 45000, jobCount: 1200, growthRate: 10.8 },
      { title: 'Medical Doctor', avgSalary: 42000, jobCount: 1800, growthRate: 8.2 },
      { title: 'Engineering Manager', avgSalary: 50000, jobCount: 900, growthRate: 14.1 }
    ],
    livingCosts: {
      rent: 650,
      food: 300,
      transport: 85,
      utilities: 130,
      entertainment: 120,
      healthcare: 90
    },
    marketStats: {
      totalJobs: 12000,
      remoteJobs: 5500,
      averageWorkWeek: 40,
      vacationDays: 25,
      healthcare: 'public',
      jobSecurity: 8.1,
      workLifeBalance: 8.4,
      techJobGrowth: 15.8
    },
    economicIndicators: {
      gdpPerCapita: 25410,
      unemploymentRate: 4.2,
      inflationRate: 2.9,
      currency: 'EUR',
      currencySymbol: '€'
    },
    qualityOfLife: {
      safetyIndex: 8.6,
      healthcareIndex: 7.8,
      educationIndex: 8.1,
      environmentIndex: 8.2
    }
  },
  {
    id: 'croatia',
    code: 'HR',
    name: 'Croatia',
    flag: '🇭🇷',
    coordinates: [15.2000, 45.1000],
    avgSalary: 28000,
    avgSalaryRange: '€18k-€45k',
    costOfLivingIndex: 58,
    taxRates: {
      income: [20, 30],
      social: 20,
      vat: 25
    },
    topEmployers: [
      { name: 'Ericsson Nikola Tesla', avgSalary: 45000, jobCount: 2800 },
      { name: 'Hrvatska Pošta', avgSalary: 32000, jobCount: 8500 },
      { name: 'Zagrebačka banka', avgSalary: 38000, jobCount: 3200 },
      { name: 'Atlantic Grupa', avgSalary: 40000, jobCount: 1800 },
      { name: 'Infobip', avgSalary: 52000, jobCount: 1200 }
    ],
    topPayingJobs: [
      { title: 'Senior Developer', avgSalary: 48000, jobCount: 2200, growthRate: 19.2 },
      { title: 'Tourism Manager', avgSalary: 35000, jobCount: 3500, growthRate: 8.5 },
      { title: 'Financial Analyst', avgSalary: 38000, jobCount: 1800, growthRate: 12.8 },
      { title: 'Medical Specialist', avgSalary: 32000, jobCount: 2800, growthRate: 6.9 },
      { title: 'Maritime Engineer', avgSalary: 42000, jobCount: 1200, growthRate: 10.5 }
    ],
    livingCosts: {
      rent: 550,
      food: 280,
      transport: 80,
      utilities: 120,
      entertainment: 110,
      healthcare: 80
    },
    marketStats: {
      totalJobs: 15000,
      remoteJobs: 6500,
      averageWorkWeek: 40,
      vacationDays: 25,
      healthcare: 'public',
      jobSecurity: 7.2,
      workLifeBalance: 8.0,
      techJobGrowth: 16.8
    },
    economicIndicators: {
      gdpPerCapita: 15829,
      unemploymentRate: 7.1,
      inflationRate: 4.2,
      currency: 'EUR',
      currencySymbol: '€'
    },
    qualityOfLife: {
      safetyIndex: 7.9,
      healthcareIndex: 6.8,
      educationIndex: 7.2,
      environmentIndex: 7.8
    }
  },
  {
    id: 'estonia',
    code: 'EE',
    name: 'Estonia',
    flag: '🇪🇪',
    coordinates: [25.0136, 58.5953],
    avgSalary: 38000,
    avgSalaryRange: '€25k-€60k',
    costOfLivingIndex: 68,
    taxRates: {
      income: [20, 20],
      social: 33,
      vat: 20
    },
    topEmployers: [
      { name: 'Skype Technologies', avgSalary: 65000, jobCount: 800 },
      { name: 'Wise', avgSalary: 72000, jobCount: 1200 },
      { name: 'Bolt', avgSalary: 58000, jobCount: 1500 },
      { name: 'Pipedrive', avgSalary: 62000, jobCount: 900 },
      { name: 'Swedbank Estonia', avgSalary: 45000, jobCount: 2200 }
    ],
    topPayingJobs: [
      { title: 'Senior Software Engineer', avgSalary: 68000, jobCount: 2500, growthRate: 22.1 },
      { title: 'Fintech Developer', avgSalary: 65000, jobCount: 1800, growthRate: 25.8 },
      { title: 'Cybersecurity Specialist', avgSalary: 58000, jobCount: 800, growthRate: 21.2 },
      { title: 'Product Manager', avgSalary: 55000, jobCount: 1200, growthRate: 18.5 },
      { title: 'Data Scientist', avgSalary: 52000, jobCount: 900, growthRate: 24.1 }
    ],
    livingCosts: {
      rent: 650,
      food: 300,
      transport: 90,
      utilities: 140,
      entertainment: 130,
      healthcare: 100
    },
    marketStats: {
      totalJobs: 8500,
      remoteJobs: 4200,
      averageWorkWeek: 40,
      vacationDays: 28,
      healthcare: 'public',
      jobSecurity: 8.2,
      workLifeBalance: 8.6,
      techJobGrowth: 23.5
    },
    economicIndicators: {
      gdpPerCapita: 23723,
      unemploymentRate: 5.6,
      inflationRate: 4.1,
      currency: 'EUR',
      currencySymbol: '€'
    },
    qualityOfLife: {
      safetyIndex: 8.8,
      healthcareIndex: 7.9,
      educationIndex: 8.4,
      environmentIndex: 8.1
    }
  },
  {
    id: 'latvia',
    code: 'LV',
    name: 'Latvia',
    flag: '🇱🇻',
    coordinates: [24.6032, 56.8796],
    avgSalary: 32000,
    avgSalaryRange: '€20k-€50k',
    costOfLivingIndex: 62,
    taxRates: {
      income: [20, 23, 31],
      social: 34.09,
      vat: 21
    },
    topEmployers: [
      { name: 'Swedbank Latvia', avgSalary: 42000, jobCount: 2800 },
      { name: 'Latvijas Banka', avgSalary: 45000, jobCount: 800 },
      { name: 'Accenture Latvia', avgSalary: 48000, jobCount: 1800 },
      { name: 'SEB banka', avgSalary: 40000, jobCount: 1500 },
      { name: 'C.T.Co', avgSalary: 52000, jobCount: 600 }
    ],
    topPayingJobs: [
      { title: 'Software Developer', avgSalary: 48000, jobCount: 2200, growthRate: 19.8 },
      { title: 'Financial Services Manager', avgSalary: 45000, jobCount: 1500, growthRate: 12.5 },
      { title: 'IT Consultant', avgSalary: 42000, jobCount: 1800, growthRate: 16.2 },
      { title: 'Medical Doctor', avgSalary: 35000, jobCount: 1200, growthRate: 8.1 },
      { title: 'Business Analyst', avgSalary: 38000, jobCount: 1000, growthRate: 14.5 }
    ],
    livingCosts: {
      rent: 600,
      food: 280,
      transport: 85,
      utilities: 130,
      entertainment: 120,
      healthcare: 90
    },
    marketStats: {
      totalJobs: 9500,
      remoteJobs: 4200,
      averageWorkWeek: 40,
      vacationDays: 28,
      healthcare: 'public',
      jobSecurity: 7.5,
      workLifeBalance: 8.1,
      techJobGrowth: 18.2
    },
    economicIndicators: {
      gdpPerCapita: 17861,
      unemploymentRate: 7.1,
      inflationRate: 3.2,
      currency: 'EUR',
      currencySymbol: '€'
    },
    qualityOfLife: {
      safetyIndex: 7.8,
      healthcareIndex: 7.1,
      educationIndex: 7.8,
      environmentIndex: 7.5
    }
  },
  {
    id: 'lithuania',
    code: 'LT',
    name: 'Lithuania',
    flag: '🇱🇹',
    coordinates: [23.8813, 55.1694],
    avgSalary: 34000,
    avgSalaryRange: '€22k-€52k',
    costOfLivingIndex: 60,
    taxRates: {
      income: [20, 27, 32],
      social: 19.5,
      vat: 21
    },
    topEmployers: [
      { name: 'Swedbank Lithuania', avgSalary: 45000, jobCount: 2200 },
      { name: 'Avanade', avgSalary: 52000, jobCount: 1500 },
      { name: 'Danske Bank', avgSalary: 48000, jobCount: 1200 },
      { name: 'Telia Lithuania', avgSalary: 42000, jobCount: 1800 },
      { name: 'Vilniaus bankas', avgSalary: 40000, jobCount: 900 }
    ],
    topPayingJobs: [
      { title: 'Senior Software Engineer', avgSalary: 55000, jobCount: 2800, growthRate: 21.5 },
      { title: 'Fintech Specialist', avgSalary: 52000, jobCount: 1200, growthRate: 23.8 },
      { title: 'Investment Analyst', avgSalary: 48000, jobCount: 800, growthRate: 15.2 },
      { title: 'Medical Specialist', avgSalary: 38000, jobCount: 1500, growthRate: 9.1 },
      { title: 'Project Manager', avgSalary: 45000, jobCount: 1800, growthRate: 16.8 }
    ],
    livingCosts: {
      rent: 580,
      food: 270,
      transport: 80,
      utilities: 125,
      entertainment: 115,
      healthcare: 85
    },
    marketStats: {
      totalJobs: 11000,
      remoteJobs: 5200,
      averageWorkWeek: 40,
      vacationDays: 28,
      healthcare: 'public',
      jobSecurity: 7.8,
      workLifeBalance: 8.3,
      techJobGrowth: 20.5
    },
    economicIndicators: {
      gdpPerCapita: 20252,
      unemploymentRate: 5.9,
      inflationRate: 4.6,
      currency: 'EUR',
      currencySymbol: '€'
    },
    qualityOfLife: {
      safetyIndex: 8.1,
      healthcareIndex: 7.6,
      educationIndex: 8.0,
      environmentIndex: 7.7
    }
  },
  // Add more countries...
  {
    id: 'sweden',
    code: 'SE',
    name: 'Sweden',
    flag: '🇸🇪',
    coordinates: [18.6435, 60.1282],
    avgSalary: 58000,
    avgSalaryRange: '€42k-€85k',
    costOfLivingIndex: 95,
    taxRates: {
      income: [32, 52],
      social: 31.42,
      vat: 25
    },
    topEmployers: [
      { name: 'Spotify', avgSalary: 95000, jobCount: 4500 },
      { name: 'Ericsson', avgSalary: 80000, jobCount: 8200 },
      { name: 'Volvo', avgSalary: 75000, jobCount: 6800 },
      { name: 'IKEA', avgSalary: 65000, jobCount: 5400 },
      { name: 'Klarna', avgSalary: 88000, jobCount: 3200 }
    ],
    topPayingJobs: [
      { title: 'Senior Software Engineer', avgSalary: 85000, jobCount: 12000, growthRate: 18.2 },
      { title: 'UX Director', avgSalary: 92000, jobCount: 2800, growthRate: 14.5 },
      { title: 'Medical Doctor', avgSalary: 95000, jobCount: 8500, growthRate: 7.2 },
      { title: 'Engineering Manager', avgSalary: 88000, jobCount: 5200, growthRate: 12.8 },
      { title: 'Data Architect', avgSalary: 82000, jobCount: 4200, growthRate: 22.1 }
    ],
    livingCosts: {
      rent: 1200,
      food: 350,
      transport: 120,
      utilities: 180,
      entertainment: 200,
      healthcare: 50
    },
    marketStats: {
      totalJobs: 45000,
      remoteJobs: 22000,
      averageWorkWeek: 37,
      vacationDays: 25,
      healthcare: 'public',
      jobSecurity: 8.8,
      workLifeBalance: 9.5,
      techJobGrowth: 20.1
    },
    economicIndicators: {
      gdpPerCapita: 51610,
      unemploymentRate: 7.2,
      inflationRate: 2.1,
      currency: 'SEK',
      currencySymbol: 'kr'
    },
    qualityOfLife: {
      safetyIndex: 9.1,
      healthcareIndex: 8.9,
      educationIndex: 8.8,
      environmentIndex: 9.2
    }
  },
  {
    id: 'norway',
    code: 'NO',
    name: 'Norway',
    flag: '🇳🇴',
    coordinates: [8.4689, 60.4720],
    avgSalary: 72000,
    avgSalaryRange: '€55k-€110k',
    costOfLivingIndex: 120,
    taxRates: {
      income: [22, 25.9],
      social: 8.2,
      vat: 25
    },
    topEmployers: [
      { name: 'Equinor', avgSalary: 95000, jobCount: 8500 },
      { name: 'DNB', avgSalary: 85000, jobCount: 6200 },
      { name: 'Telenor', avgSalary: 80000, jobCount: 5800 },
      { name: 'Yara', avgSalary: 88000, jobCount: 4200 },
      { name: 'Opera', avgSalary: 92000, jobCount: 2800 }
    ],
    topPayingJobs: [
      { title: 'Petroleum Engineer', avgSalary: 125000, jobCount: 3200, growthRate: 2.1 },
      { title: 'Software Architect', avgSalary: 98000, jobCount: 8500, growthRate: 16.8 },
      { title: 'Marine Engineer', avgSalary: 105000, jobCount: 2800, growthRate: 5.2 },
      { title: 'Data Scientist', avgSalary: 88000, jobCount: 4500, growthRate: 24.2 },
      { title: 'Financial Director', avgSalary: 115000, jobCount: 2200, growthRate: 8.1 }
    ],
    livingCosts: {
      rent: 1600,
      food: 450,
      transport: 150,
      utilities: 200,
      entertainment: 250,
      healthcare: 100
    },
    marketStats: {
      totalJobs: 38000,
      remoteJobs: 18000,
      averageWorkWeek: 37.5,
      vacationDays: 25,
      healthcare: 'public',
      jobSecurity: 9.1,
      workLifeBalance: 9.3,
      techJobGrowth: 17.5
    },
    economicIndicators: {
      gdpPerCapita: 75420,
      unemploymentRate: 3.9,
      inflationRate: 1.8,
      currency: 'NOK',
      currencySymbol: 'kr'
    },
    qualityOfLife: {
      safetyIndex: 9.3,
      healthcareIndex: 9.1,
      educationIndex: 8.9,
      environmentIndex: 9.4
    }
  }
];